import AiLabel from "@/components/ai-label";
import Category from "@/components/category";
import Icon from "@/components/icon";
import TeaserDownpull from "@/components/teaser-downpull";
import TeaserHeading from "@/components/teaser-heading";
import TeaserLinkedContent from "@/components/teaser-linked-content";
import { Content } from "@/services/content/types";
import { createImageSizes, createImageUrl } from "@/utils/image";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";

type MediumProps = {
  specialByline?: React.ReactNode;
  article: Content.Article;
  hideImage?: boolean;
  hideDownPull?: boolean;
  showByline?: boolean;
  bylineImage?: boolean;
  imagePriority?: boolean;
};

const Large = (props: MediumProps) => {
  const author = props.article.fields.authors?.[0];
  const category = props.article.fields.categories?.[0];
  const title = props.article.fields.teaserTitle || props.article.fields.title;
  const hasLinkedContent = Boolean(props.article.fields.linkedContent?.length);
  const hideBylineImage = props.article.fields.hideBylineImage;
  const image =
    props.article.fields.teaserImage || props.article.fields.images?.[0];

  const showByline =
    props.article.fields.showByline !== undefined
      ? props.article.fields.showByline
      : false;

  return (
    <div>
      <Link
        href={`/${props.article.fields.slug}`}
        className={classNames(
          "bg-sand-600 group flex flex-col text-gray-500 relative pb-4 overflow-hidden",
          { "border-b border-sand-800": hasLinkedContent },
        )}
      >
        {image?.fields?.file?.url && !props.hideImage && (
          <div className="aspect-w-16 aspect-h-9">
            <Image
              priority={props.imagePriority ? true : false}
              fill
              src={createImageUrl(image.fields.file.url, { crop: "16:9" })}
              alt={image.fields.alt || ""}
              className="object-cover"
              sizes={createImageSizes({
                mobile: "25vw",
                tablet: "25vw",
                desktop: "25vw",
              })}
            />
            <AiLabel image={image} />
          </div>
        )}
        <div className="p-4 md:p-8 relative">
          <Icon.Quote className="fill-sand-400 ml-4 mb-0 absolute -top-12 left-0 w-22 h-auto" />
          {category && <Category category={category} />}
          {title && (
            <TeaserHeading
              text={title}
              className="text-3xl md:text-5xl relative font-semibold max-w-[70%]"
            />
          )}
          {!props.hideDownPull && (
            <div className="max-w-[65%]">
              <TeaserDownpull
                showByline={showByline}
                authorName={author?.fields.name}
                excerpt={props.article.fields.excerpt}
                showbyLineImage={showByline}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-auto ml-4 md:ml-8 md:mb-2">
          {author?.fields.name && (
            <div className="font-brush text-xl">{author.fields.name}</div>
          )}

          {author?.fields.image?.fields.file?.url && (
            <div className="w-[400px] h-[250px] max-w-[40%] absolute right-0 bottom-0 -mr-[5%]">
              <Image
                src={createImageUrl(author.fields.image.fields.file.url, {
                  crop: "1:1",
                })}
                alt=""
                fill
                sizes={createImageSizes({
                  mobile: "25vw",
                  tablet: "25vw",
                  desktop: "25vw",
                })}
                className="object-contain object-right-bottom"
              />
            </div>
          )}
        </div>
      </Link>
      {hasLinkedContent && (
        <div
          className={classNames(
            "border-b border-b-sand-800 bg-sand-600 -mt-4 last:border-0 p-4",
          )}
        >
          <TeaserLinkedContent
            linkedContent={props.article.fields.linkedContent}
            showLinkedContent={hasLinkedContent}
            compactMode={false}
          />
        </div>
      )}
      {props.specialByline}
    </div>
  );
};

export default Large;
